import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';

/**
 * @description Initializes PostHog analytics with the provided brand and source information. This function checks for the necessary environment variables and sets up PostHog with appropriate configurations, including feature flag handling and user property registration.
 * @param {Object} params - The parameters for initializing PostHog.
 * @param {string} params.brand - The brand associated with the analytics data.
 * @param {string} params.source - The source of the analytics data.
 * @returns {Promise<void>} A promise that resolves when PostHog is successfully initialized, or rejects if there is an error during initialization.
 * @throws {Error} Throws an error if the PostHog API key or host is not defined in the environment variables.
 */
export const initPostHog = ({
  brand,
  source,
}: {
  brand: string;
  source: string;
}): Promise<void> => {
  const { VITE_POSTHOG_API_KEY, VITE_POSTHOG_HOST } = import.meta.env;

  log.debug({ message: 'Initializing PostHog', data: { brand, source } });

  return new Promise<void>((resolve, reject) => {
    if (!VITE_POSTHOG_API_KEY || !VITE_POSTHOG_HOST) {
      return reject(new Error('PostHog API key or host is not defined in environment variables'));
    }

    posthog.init(VITE_POSTHOG_API_KEY, {
      api_host: VITE_POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      persistence: 'localStorage',
      loaded: (ph) => {
        // On feature flags gets invoked even if the flags request is successful or unsuccessful, so we can use it to ensure that PostHog has finished its initialization process
        // Complete in 3 seconds if the flags request fails to prevent hanging the app indefinitely
        ph.onFeatureFlags(() => {
          resolve();
        });

        ph.register({
          brand,
          source,
        });
      },
    });
  });
};
